import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from '../components/SignaturePad';
import '../styles/HptScreens.css';

// BackEnd de Serveless
const API_URL = process.env.REACT_APP_API_URL;

// ============================================================================
// COMPONENTE: ExpandableCard (Acordeón reutilizable)
// ============================================================================
const ExpandableCard = ({ title, icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="expandable-card">
            <div className="card-header" onClick={() => setIsOpen(!isOpen)}>
                <span>{icon} {title}</span>
                <span>{isOpen ? '▲' : '▼'}</span>
            </div>
            <div className={`card-content ${isOpen ? 'open' : ''}`}>
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// COMPONENTE: Gestor de Descargos (Bloqueos LOTO)
// ============================================================================
const DescargoManager = ({ label, count, setCount, refs, setRefs }) => {
    // Función para modificar la referencia escrita en una posición concreta del array
    const handleChange = (idx, val) => {
        const newRefs = [...refs];
        newRefs[idx] = val;
        setRefs(newRefs);
    };

    return (
        <div className="descargo-col">
            <label className="mini-label">{label}:</label>
            {/* Input que define cuántos descargos hay de este tipo */}
            <input 
                type="number" min="0" 
                value={count} 
                onChange={e => setCount(parseInt(e.target.value) || 0)} 
                className="num-input" 
            />
            {/* Genera dinámicamente X inputs de texto según el número indicado antes*/}
            {[...Array(count)].map((_, i) => (
                <input 
                    key={i} 
                    placeholder="Ref..." 
                    className="ref-input" 
                    value={refs[i] || ''}
                    onChange={(e) => handleChange(i, e.target.value)}
                />
            ))}
        </div>
    );
};

// ============================================================================
// COMPONENTE PRINCIPAL: DetailsPTWScreen
// ============================================================================
const DetailsPTWScreen = () => {
    const { id } = useParams(); // Saca el ID interno del PTW desde la URL
    const navigate = useNavigate();
    
    // --- ESTADOS PRINCIPALES ---
    const [ptw, setPtw] = useState(null);               // Objeto completo traído del servidor
    const [activos, setActivos] = useState([]);         // Lista para el buscador
    const [catalogo, setCatalogo] = useState([]);       // Catálogo de seguridad
    const [seleccionados, setSeleccionados] = useState([]); // IDs de riesgos/medidas
    const [loading, setLoading] = useState(true);       // Spinner de carga inicial

    // --- ESTADOS DE FIRMAS ---
    const [showSignature, setShowSignature] = useState(false); // Muestra/Oculta el pad negro
    const [renovaciones, setRenovaciones] = useState([]);      // Array para las jornadas extra
    const [currentSignType, setCurrentSignType] = useState(null); // Guarda quién va a firmar (sección y rol)

    // --- ESTADOS DEL BUSCADOR DE ACTIVOS ---
    const [busquedaActivo, setBusquedaActivo] = useState('');
    const [mostrarDesplegable, setMostrarDesplegable] = useState(false);
    const [opcionesActivos, setOpcionesActivos] = useState([]);

    // --- ESTADOS DE DESCARGOS (Operación y Mantenimiento) ---
    const [nElecOp, setNElecOp] = useState(0);
    const [nDescElecOp, setNDescElecOp] = useState(0);
    const [nDescMecOp, setNDescMecOp] = useState(0);
    const [nElecMan, setNElecMan] = useState(0);
    const [nDescElecMan, setNDescElecMan] = useState(0);
    const [nDescMecMan, setNDescMecMan] = useState(0);
    
    const [refsElecOp, setRefsElecOp] = useState([]);
    const [refsDescElecOp, setRefsDescElecOp] = useState([]);
    const [refsDescMecOp, setRefsDescMecOp] = useState([]);
    const [refsElecMan, setRefsElecMan] = useState([]);
    const [refsDescElecMan, setRefsDescElecMan] = useState([]);
    const [refsDescMecMan, setRefsDescMecMan] = useState([]);

    // --- MATRIZ DE FIRMAS (ESTADO INICIAL) ---
    // Esta es la estructura base. Si la BBDD ya tiene firmas, se sobrescribe en el fetchData
    const [firmasSecciones, setFirmasSecciones] = useState({
        consignacion: {
            'Jefe de Turno': { nombre: '', firma_path: null, fecha_hora: null },
            'Jefe de Trabajo': { nombre: '', firma_path: null, fecha_hora: null },
            'Resp. Contrata': { nombre: '', firma_path: null, fecha_hora: null }
        },
        medidas: {
            'Jefe de Trabajo': { nombre: '', firma_path: null, fecha_hora: null },
            'Recurso Preventivo': { nombre: '', firma_path: null, fecha_hora: null },
            'Resp. Contrata': { nombre: '', firma_path: null, fecha_hora: null }
        },
        apertura: {
            'Jefe Trabajo': { nombre: '', telefono: '', firma_path: null, fecha_hora: null },
            'Jefe de Turno': { nombre: '', telefono: '', firma_path: null, fecha_hora: null },
            'Recurso Prev.': { nombre: '', telefono: '', firma_path: null, fecha_hora: null },
            'Resp. Contrata': { nombre: '', telefono: '', firma_path: null, fecha_hora: null }
        },
        cierre: {
            'Jefe Trabajo': { nombre: '', firma_path: null, fecha_hora: null },
            'Jefe de Turno': { nombre: '', firma_path: null, fecha_hora: null },
            'Comprobado en Campo': { nombre: '', firma_path: null, fecha_hora: null }
        }
    });

    // ============================================================================
    // CARGA INICIAL
    // ============================================================================
    const fetchData = useCallback(async () => {
        const token = localStorage.getItem('userToken');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            // Descargamos en paralelo para mayor velocidad
            const [resPtw, resActivos, resCatalogo] = await Promise.all([
                axios.get(`${API_URL}/api/ptw/${id}`, config),
                axios.get(`${API_URL}/api/catalogos/activos`, config),
                axios.get(`${API_URL}/api/catalogos/seguridad`, config)
            ]);
            
            let data = resPtw.data;

            // 1. Resturamos Firmas Fijas Guardadas
            if (data.json_firmas && typeof data.json_firmas === 'object' && Object.keys(data.json_firmas).length > 0) {
                setFirmasSecciones(data.json_firmas);
            }
            
            // 2. Resturamos Renovaciones Guardadas
            if (data.json_renovaciones && Array.isArray(data.json_renovaciones)) {
                setRenovaciones(data.json_renovaciones);
            }

            // 3. Restauramos Descargos (si los hay)
            if (data.json_descargos) {
                const d = data.json_descargos;
                // Operación
                setNElecOp(d.operacion?.nElecOp || 0);
                setNDescElecOp(d.operacion?.nDescElecOp || 0);
                setNDescMecOp(d.operacion?.nDescMecOp || 0);
                setRefsElecOp(d.operacion?.refsElecOp || []);
                setRefsDescElecOp(d.operacion?.refsDescElecOp || []);
                setRefsDescMecOp(d.operacion?.refsDescMecOp || []);

                // Mantenimiento
                setNElecMan(d.mantenimiento?.nElecMan || 0);
                setNDescElecMan(d.mantenimiento?.nDescElecMan || 0);
                setNDescMecMan(d.mantenimiento?.nDescMecMan || 0);
                setRefsElecMan(d.mantenimiento?.refsElecMan || []);
                setRefsDescElecMan(d.mantenimiento?.refsDescElecMan || []);
                setRefsDescMecMan(d.mantenimiento?.refsDescMecMan || []);
            }

            // 4. Si faltan nombres de responsables, los cogemos de la HPT original vinculada (si existe)
            if (!data.solicitante_nombre || data.solicitante_nombre.trim() === "") {
                data.solicitante_nombre = data.hpt?.nombre_completo_creador || "";
            }
            if (!data.recurso_preventivo_nombre || data.recurso_preventivo_nombre.trim() === "") {
                data.recurso_preventivo_nombre = data.hpt?.nombre_preventivo_hpt || "";
            }

            // 5. Cargar Riesgos/Medidas (Se prioriza los del PTW sobre HPT (En revision))
            if (data.riesgos_ptw_manual && data.riesgos_ptw_manual.length > 0) {
                setSeleccionados(data.riesgos_ptw_manual);
            } else {
                const idsHpt = [
                    ...(data.hpt?.riesgos_detallados?.map(r => r.id_item) || []),
                    ...(data.hpt?.medidas_detalladas?.map(m => m.id_item) || [])
                ];
                setSeleccionados(idsHpt);
            }

            setPtw(data);
            setBusquedaActivo(data.codigo_activo || '');
            setCatalogo(resCatalogo.data);
            setLoading(false);

        } catch (err) { 
            console.error("Error cargando PTW:", err);
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ============================================================================
    // FUNCIONES DE AUTOCOMPLETAR (ACTIVOS)
    // ============================================================================
    const buscarActivosEnServidor = async (texto) => {
        setBusquedaActivo(texto);
        if (texto.length < 2) {
            setOpcionesActivos([]);
            return;
        }
        try {
            const token = localStorage.getItem('userToken');
            const res = await axios.get(`${API_URL}/api/catalogos/activos/buscar?q=${texto}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpcionesActivos(res.data.results || []);
        } catch (err) {
            console.error("Error buscando activos:", err);
        }
    };

    const seleccionarActivo = (activo) => {
        setPtw({
            ...ptw,
            id_activo: activo.id_activo,
            den_act_snapshot: activo.denominacion
        });
        setBusquedaActivo(activo.codigo_activo);
        setMostrarDesplegable(false);
    };

    // ============================================================================
    // Guardar en servidor
    // ============================================================================
    const handleGuardarTodo = async () => {
        const token = localStorage.getItem('userToken');
        
        // Antes de guardar, miramos las firmas para ver si el PTW avanza de estado
        const nuevoEstado = calcularNuevoEstado();
        
        // Evitamos enviar cadenas vacías (""), las convertimos a null para que la DB no falle
        const clean = (val) => (val === "" || val === undefined ? null : val);

        const payload = {
            alcance: clean(ptw.alcance),
            estado: nuevoEstado,
            solicitante_nombre: clean(ptw.solicitante_nombre),
            recurso_preventivo_nombre: clean(ptw.recurso_preventivo_nombre),
            id_activo: ptw.id_activo ? parseInt(ptw.id_activo) : null,
            ot_asociada: clean(ptw.ot_asociada),
            fecha_trabajo: clean(ptw.fecha_trabajo),
            rango_horario: clean(ptw.rango_horario),
            numero_caja: clean(ptw.numero_caja),
            candado_operacion: clean(ptw.candado_operacion),
            candado_ejecutante: clean(ptw.candado_ejecutante),
            observaciones: clean(ptw.observaciones),
            
            // Las matrices de datos complejas van enteras
            firmas_secciones: firmasSecciones,
            renovaciones: renovaciones,
            seleccionados_seguridad: seleccionados,
            descargos: {
                operacion: { nElecOp, nDescElecOp, nDescMecOp, refsElecOp, refsDescElecOp, refsDescMecOp },
                mantenimiento: { nElecMan, nDescElecMan, nDescMecMan, refsElecMan, refsDescElecMan, refsDescMecMan }
            }
        };

        try {
            await axios.put(`${API_URL}/api/ptw/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPtw({...ptw, estado: nuevoEstado}); 
            alert(`✅ Sincronizado. El permiso ahora está en estado: ${nuevoEstado.toUpperCase()}`);
        } catch (err) {
            console.error("Error al guardar:", err.response?.data);
            alert("❌ Error al guardar. Verifica la conexión con el servidor.");
        }
    };

    // ============================================================================
    // GESTIÓN DE FIRMAS (PAD)
    // ============================================================================
    // Se ejecuta al dar clic en el botón de firmar
    const handleOpenSignature = (type, section, idx = null, rol = null) => {
        // Guardamos las coordenadas (Ej: type:'fija', section:'apertura', rol:'Jefe Trabajo')
        setCurrentSignType({ type, section, idx, rol });
        setShowSignature(true); // Abre el Pad de firmas
    };

    // Se ejecuta cuando el Pad nos devuelve la imagen Base64
    const handleSaveSignature = (signatureBase64) => {
        const now = new Date().toLocaleString(); // Registramos la hora exacta de la firma

        if (currentSignType.type === 'renovacion') {
            // Guarda en la sección de renovaciones
            const nuevas = [...renovaciones];
            nuevas[currentSignType.idx].firmas[currentSignType.rol].firma_path = signatureBase64;
            nuevas[currentSignType.idx].firmas[currentSignType.rol].fecha_hora = now;
            setRenovaciones(nuevas);
        } else {
            // Guarda en la sección de Firmas Fijas (Apertura, Cierre, etc.)
            setFirmasSecciones(prev => ({
                ...prev,
                [currentSignType.section]: {
                    ...prev[currentSignType.section],
                    [currentSignType.rol]: {
                        ...prev[currentSignType.section][currentSignType.rol],
                        firma_path: signatureBase64,
                        fecha_hora: now
                    }
                }
            }));
        }
        setShowSignature(false); // Cierra el Pad
    };

    // Actualiza textos como el nombre o el telefono.
    const updateFirmaData = (section, rol, field, val) => {
        setFirmasSecciones(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [rol]: { ...prev[section][rol], [field]: val }
            }
        }));
    };

    const toggleItem = (idItem) => {
        setSeleccionados(prev => prev.includes(idItem) ? prev.filter(i => i !== idItem) : [...prev, idItem]);
    };

    // Añade bloque vacío para cada renovación
    const addRenovacion = () => {
        const roles = ['Jefe Trabajo', 'Jefe de Turno', 'Recurso Prev.'];
        // Si hay subcontrata implicada, añadimos el hueco de firma para su Responsable
        if (ptw.hpt?.interviene_subcontrata) roles.push('Resp. Contrata');
        
        const nueva = {
            id_temp: Date.now(),
            fecha: new Date().toISOString().split('T')[0],
            firmas: roles.reduce((acc, rol) => {
                acc[rol] = { nombre: '', telefono: '', firma_path: null, fecha_hora: null };
                return acc;
            }, {})
        };
        setRenovaciones([...renovaciones, nueva]);
    };

    // ============================================================================
    // LÓGICA DE PTW
    // ============================================================================
    if (loading) return <div style={{padding: '50px'}}>Cargando PTW...</div>;

    const intervieneSubcontrata = ptw.hpt?.interviene_subcontrata === true;
    
    // Extraemos los riegos y medidas
    const categoriasRiesgo = [...new Set(catalogo.filter(i => i.es_riesgo).map(i => i.categoria))];
    const categoriasMedida = [...new Set(catalogo.filter(i => !i.es_riesgo).map(i => i.categoria))];

    // Consignación LOTO
    const necesitaConsignacion = nElecOp > 0 || nDescElecOp > 0 || nDescMecOp > 0;

    // FUNCIÓN MAESTRA: Revisa si una sección entera (ej: Cierre) tiene TODAS sus firmas
    const seccionEstaCompleta = (seccion) => {
        const firmas = firmasSecciones[seccion];
        if (!firmas) return false;

        let rolesRequeridos = Object.keys(firmas);
        // Filtramos para ignorar la firma de subcontrata si no aplica
        if (!intervieneSubcontrata) {
            rolesRequeridos = rolesRequeridos.filter(rol => rol !== 'Resp. Contrata');
        }

        // Devuelve TRUE solo si TODOS los roles obligatorios tienen imagen (firma_path) y nombre escrito
        return rolesRequeridos.every(rol => 
            firmas[rol]?.firma_path !== null && 
            firmas[rol]?.nombre && 
            firmas[rol]?.nombre.trim() !== ""
        );
    };

    // LÓGICA DE FIRMAS: Bloquea los apartados de firmas que no deben ser firmado sin hacer los anteriores antes
    const puedeFirmarApertura = seccionEstaCompleta('medidas');

    // Autocalcula el estado del PTW basándose en las secciones completadas
    const calcularNuevoEstado = () => {
        if (seccionEstaCompleta('cierre')) return "cerrado";
        
        const hayRenovacion = renovaciones.some(ren => 
            Object.values(ren.firmas).some(f => f.firma_path !== null)
        );
        if (hayRenovacion) return "renovado";
        
        if (seccionEstaCompleta('apertura')) return "abierto";
        
        return ptw.estado || "creado";
    };

    // Configuración de la UI para el Estado
    const getStatusConfig = (estado) => {
        switch (estado) {
            case 'cerrado': return { icon: '🏁', color: '#d32f2f' };
            case 'abierto': return { icon: '🔓', color: '#2e7d32' };
            case 'renovado': return { icon: '⏳', color: '#1a237e' };
            default: return { icon: '📝', color: '#f57c00' };
        }
    };

    // ============================================================================
    // RENDER (HTML/JSX)
    // ============================================================================
    return (
        <div className="container">
            {/* 1. ENCABEZADO CON GUARDADO */}
            <div className="top-header header-ptw">
                <div className="d-flex align-center">
                    <span className="ptw-label">PERMISO:</span>
                    <div className="ptw-badge-alt">
                        <span style={{ fontSize: '20px' }}>📄</span> 
                        {ptw.id_ptw_formato}
                    </div>
                </div>
                
                {/* Estado PTW Autocalculado */}
                <div className="d-flex align-center mt-10 mb-20">
                    <span className="ptw-label">ESTADO DEL PERMISO:</span>
                    <div className="status-badge-main" style={{ backgroundColor: getStatusConfig(ptw.estado).color }}>
                        <span style={{ fontSize: '18px' }}>{getStatusConfig(ptw.estado).icon}</span>
                        {ptw.estado ? ptw.estado.toUpperCase() : 'CREADO'}
                    </div>
                </div>
                <div className="header-actions">
                    <button onClick={handleGuardarTodo} className="btn-save">💾 Guardar Cambios</button>
                    <button className="btn-print">🖨️ Imprimir PDF</button>
                    <button onClick={() => navigate('/dashboard')} className="btn-close">Cerrar</button>
                </div>
            </div>

            {/* 2. DATOS GENERALES */}
            <div className="main-card">
                <div className="row">
                    <div className="d-flex flex-1" style={{ flexDirection:'column', flex: 2 }}>
                        <label className="info-label">ALCANCE:</label>
                        <textarea 
                            className="main-textarea"
                            value={ptw.alcance || ''} 
                            onChange={e => setPtw({...ptw, alcance: e.target.value})} 
                        />
                    </div>
                    <div className="field">
                        <label className="info-label">SOLICITANTE:</label>
                        <input 
                            className="input-solicitante"
                            value={ptw.solicitante_nombre} 
                            onChange={e => setPtw({...ptw, solicitante_nombre: e.target.value})} 
                        />
                    </div>
                </div>
                
                {/* BUSCADOR AUTOCOMPLETAR ACTIVOS */}
                <div className="asset-section-hpt" style={{ marginBottom: '15px' }}>
                    <div className="row">
                        <div className="info-box flex-1 relative">
                            <span className="info-label">CÓDIGO ACTIVO (Buscador):</span>
                            
                            <input 
                                type="text" 
                                className="main-input" 
                                placeholder="Escriba código o nombre..."
                                value={busquedaActivo}
                                onChange={(e) => {
                                    buscarActivosEnServidor(e.target.value);
                                    setMostrarDesplegable(true);
                                    if (ptw.id_activo) {
                                        setPtw({...ptw, id_activo: null, den_act_snapshot: ''});
                                    }
                                }}
                                onFocus={() => setMostrarDesplegable(true)}
                                onBlur={() => setTimeout(() => setMostrarDesplegable(false), 200)}
                            />

                            {mostrarDesplegable && busquedaActivo.length >= 2 && (
                                <ul className="autocomplete-list">
                                    {opcionesActivos.length > 0 ? (
                                        opcionesActivos.map(a => (
                                            <li 
                                                key={a.id_activo} 
                                                className="autocomplete-item"
                                                onMouseDown={() => seleccionarActivo(a)} 
                                            >
                                                <strong>{a.codigo_activo}</strong> <br/>
                                                <span className="text-small text-muted">{a.denominacion}</span>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="autocomplete-item">No se encontraron activos...</li>
                                    )}
                                </ul>
                            )}
                        </div>
                        
                        <div className="info-box flex-2 asset-display-box">
                            <span className="info-label">DENOMINACIÓN DEL EQUIPO:</span>
                            <div className="asset-name-text">
                                {ptw.den_act_snapshot || 'Seleccione un activo en el buscador...'}
                            </div>
                        </div>

                        <div className="info-box flex-1">
                            <label className="info-label">OT ASOCIADA:</label>
                            <input value={ptw.ot_asociada || ''} onChange={e => setPtw({...ptw, ot_asociada: e.target.value})} className="main-input" />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="field">
                        <label className="info-label">FECHA:</label>
                        <input type="date" value={ptw.fecha_trabajo || ''} onChange={e => setPtw({...ptw, fecha_trabajo: e.target.value})} className="main-input" />
                    </div>
                    <div className="field">
                        <label className="info-label">HORARIO:</label>
                        <input value={ptw.rango_horario || ''} onChange={e => setPtw({...ptw, rango_horario: e.target.value})} className="main-input" />
                    </div>
                </div>
            </div>

            {/* 3. DESCARGOS OPERACIÓN */}
            <ExpandableCard title="Descargos de Operación" icon="⚙️">
                <div className="descargos-grid">
                    <DescargoManager label="Bloqueos Eléctricos" count={nElecOp} setCount={setNElecOp} refs={refsElecOp} setRefs={setRefsElecOp} />
                    <DescargoManager label="Descargos Eléctricos" count={nDescElecOp} setCount={setNDescElecOp} refs={refsDescElecOp} setRefs={setRefsDescElecOp} />
                    <DescargoManager label="Descargos Mecánicos" count={nDescMecOp} setCount={setNDescMecOp} refs={refsDescMecOp} setRefs={setRefsDescMecOp} />
                </div>
            </ExpandableCard>

            {/* 4. VALIDACIÓN CONSIGNACIÓN */}
            <ExpandableCard title="Validación Consignación y Estado" icon="✍️">
                {!necesitaConsignacion ? (
                    // Mensaje de aviso si no aplica LOTO
                    <p style={{ color: '#666', fontStyle: 'italic', fontSize: '13px' }}>
                        ℹ️ No se requieren firmas de consignación (sin descargos de operación activos).
                    </p>
                ) : (
                    <div className="signature-row">
                        {['Jefe de Turno', 'Jefe de Trabajo'].concat(intervieneSubcontrata ? ['Resp. Contrata'] : []).map(rol => (
                            <div key={rol} className="sign-box-full">
                                <p><strong>{rol}</strong></p>
                                <div className="placeholder-sign">
                                    {firmasSecciones.consignacion[rol]?.firma_path ? (
                                        <img src={firmasSecciones.consignacion[rol].firma_path} style={{height:'100%'}} alt="sig"/>
                                    ) : (
                                        <button 
                                            className="sign-btn-small" 
                                            onClick={() => handleOpenSignature('fija', 'consignacion', null, rol)}
                                        >
                                            Firmar
                                        </button>
                                    )}
                                </div>
                                <input 
                                    placeholder="Nombre completo" 
                                    className="mini-input-table"
                                    value={firmasSecciones.consignacion[rol]?.nombre || ''} 
                                    onChange={e => updateFirmaData('consignacion', rol, 'nombre', e.target.value)} 
                                />
                                {firmasSecciones.consignacion[rol]?.fecha_hora && (
                                    <div className="timestamp-tag">{firmasSecciones.consignacion[rol].fecha_hora}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </ExpandableCard>

            {/* 5. BLOQUEOS MANTENIMIENTO */}
            <ExpandableCard title="Bloqueos de Mantenimiento y Caja" icon="🔒">
                <div className="descargos-grid">
                    <DescargoManager label="Bloqueos Eléctricos" count={nElecMan} setCount={setNElecMan} refs={refsElecMan} setRefs={setRefsElecMan} />
                    <DescargoManager label="Descargos Eléctricos" count={nDescElecMan} setCount={setNDescElecMan} refs={refsDescElecMan} setRefs={setRefsDescElecMan} />
                    <DescargoManager label="Descargos Mecánicos" count={nDescMecMan} setCount={setNDescMecMan} refs={refsDescMecMan} setRefs={setRefsDescMecMan} />
                </div>
                <div className="safe-box-grid">
                    <div className="field">
                        <label className="info-label">Nº CAJA:</label>
                        <input className="main-input" value={ptw.numero_caja || ''} onChange={e => setPtw({...ptw, numero_caja: e.target.value})} />
                    </div>
                    <div className="field">
                        <label className="info-label">CANDADOS OP:</label>
                        <input className="main-input" value={ptw.candado_operacion || ''} onChange={e => setPtw({...ptw, candado_operacion: e.target.value})} />
                    </div>
                    <div className="field">
                        <label className="info-label">CANDADOS EJECUTANTE:</label>
                        <input className="main-input" value={ptw.candado_ejecutante || ''} onChange={e => setPtw({...ptw, candado_ejecutante: e.target.value})} />
                    </div>
                </div>
            </ExpandableCard>

            {/* 6. RIESGOS */}
            <ExpandableCard title="Evaluación de Riesgos" icon="🚨">
                <div className="category-grid">
                    {categoriasRiesgo.map(cat => (
                        <div key={cat} className="cat-block">
                            <div className="cat-title">{cat}</div>
                            <div className="grid-checks-auto">
                                {catalogo.filter(i => i.categoria === cat && i.es_riesgo).map(item => (
                                    <label key={item.id_item} className={`check-item ${seleccionados.includes(item.id_item) ? 'checked-risk' : ''}`}>
                                        <input type="checkbox" checked={seleccionados.includes(item.id_item)} onChange={() => toggleItem(item.id_item)} />
                                        <span><strong>{item.codigo}</strong> - {item.descripcion}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </ExpandableCard>

            {/* 7. FIRMAS MEDIDAS */}
            <ExpandableCard title="Firmas Validación de Medidas" icon="✍️">
                <div className="signature-row">
                    {['Jefe de Trabajo', 'Recurso Preventivo'].concat(intervieneSubcontrata ? ['Resp. Contrata'] : []).map(rol => (
                        <div key={rol} className="sign-box-full">
                            <p><strong>{rol}</strong></p>
                            <div className="placeholder-sign">
                                {firmasSecciones.medidas[rol]?.firma_path ? (
                                    <img src={firmasSecciones.medidas[rol].firma_path} style={{height:'100%'}} alt="sig"/> 
                                ) : (
                                    <button className="sign-btn-small" onClick={() => handleOpenSignature('fija', 'medidas', null, rol)}>Firmar</button>
                                )}
                            </div>
                            <input 
                                placeholder="Nombre" 
                                className="mini-input-table"
                                value={firmasSecciones.medidas[rol]?.nombre || ''} 
                                onChange={e => updateFirmaData('medidas', rol, 'nombre', e.target.value)} 
                            />
                            {firmasSecciones.medidas[rol]?.fecha_hora && (
                                <div className="timestamp-tag">{firmasSecciones.medidas[rol].fecha_hora}</div>
                            )}
                        </div>
                    ))}
                </div>
            </ExpandableCard>

            {/* 8. MEDIDAS CORRECTORAS */}
            <ExpandableCard title="Medidas Correctoras Aplicadas" icon="🛡️">
                <div className="category-grid">
                    {categoriasMedida.map(cat => (
                        <div key={cat} className="cat-block">
                            <div className="cat-title">{cat}</div>
                            <div className="grid-checks-auto">
                                {catalogo.filter(i => i.categoria === cat && !i.es_riesgo).map(item => (
                                    <label key={item.id_item} className={`check-item ${seleccionados.includes(item.id_item) ? 'checked-measure' : ''}`}>
                                        <input type="checkbox" checked={seleccionados.includes(item.id_item)} onChange={() => toggleItem(item.id_item)} />
                                        <span>{item.descripcion}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </ExpandableCard>

            {/* 9. OBSERVACIONES Y RECURSO */}
            <ExpandableCard title="Observaciones y Recursos" icon="📝">
                <div className="field">
                    <label className="info-label">RECURSO PREVENTIVO DESIGNADO (EDITABLE):</label>
                    <input 
                        className="input-solicitante"
                        value={ptw.recurso_preventivo_nombre} 
                        onChange={e => setPtw({...ptw, recurso_preventivo_nombre: e.target.value})} 
                    />
                </div>
                <label className="info-label mt-15 d-flex">OBSERVACIONES DE EJECUCIÓN:</label>
                <textarea 
                    className="main-textarea"
                    rows="2" 
                    value={ptw.observaciones || ''} 
                    onChange={e => setPtw({...ptw, observaciones: e.target.value})} 
                />
            </ExpandableCard>

            {/* --- 10. APERTURA DEL PERMISO --- */}
            <ExpandableCard title="Apertura del Permiso" icon="🔓" defaultOpen={ptw.estado === 'creado'}>
                
                {/* LOGICA DE ESTADO DE FIRMAS. Se BLOQUEA si el anterior no esta completado*/}
                {!puedeFirmarApertura && (
                    <div className="alert-lock-banner">
                        <span style={{ fontSize: '24px' }}>🚫</span>
                        <div>
                            <strong>BLOQUEO DE SEGURIDAD:</strong> <br/>
                            Para habilitar la apertura, primero deben completarse todos los nombres y firmas en el acordeón de "Firmas Validación de Medidas".
                        </div>
                    </div>
                )}

                {/* Reducimos opacidad si está bloqueado */}
                <div className="signature-grid" style={{ opacity: puedeFirmarApertura ? 1 : 0.5 }}>
                    {['Jefe Trabajo', 'Jefe de Turno', 'Recurso Prev.'].concat(intervieneSubcontrata ? ['Resp. Contrata'] : []).map(rol => (
                        <div key={rol} className="sign-box-full">
                            <p style={{fontSize: '14px', marginBottom: '10px'}}><strong>{rol}</strong></p>
                            
                            <div className="placeholder-sign">
                                {firmasSecciones.apertura[rol]?.firma_path ? (
                                    <img src={firmasSecciones.apertura[rol].firma_path} style={{height:'100%'}} alt="sig"/>
                                ) : (
                                    <button 
                                        className="sign-btn-small"
                                        onClick={() => handleOpenSignature('fija', 'apertura', null, rol)}
                                        disabled={!puedeFirmarApertura} // Bloquea el botón HTML real
                                    >
                                        Firmar Apertura
                                    </button>
                                )}
                            </div>

                            <input 
                                placeholder="Nombre completo" 
                                className="mini-input-table"
                                value={firmasSecciones.apertura[rol]?.nombre || ''} 
                                onChange={e => updateFirmaData('apertura', rol, 'nombre', e.target.value)} 
                                disabled={!puedeFirmarApertura}
                            />
                            <input 
                                placeholder="Teléfono" 
                                className="mini-input-table"
                                value={firmasSecciones.apertura[rol]?.telefono || ''} 
                                onChange={e => updateFirmaData('apertura', rol, 'telefono', e.target.value)} 
                                disabled={!puedeFirmarApertura}
                            />
                            
                            {firmasSecciones.apertura[rol]?.fecha_hora && (
                                <div className="timestamp-tag">
                                    ⏱️ {firmasSecciones.apertura[rol].fecha_hora}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </ExpandableCard>

            {/* 11. RENOVACIONES */}
            <ExpandableCard title="Renovaciones de Jornada" icon="⏳">
                <button type="button" onClick={addRenovacion} className="btn-add-item">
                    + Abrir Nueva Jornada (Renovación)
                </button>
                {/* Imprimimos tantas cajas de firmas como jornadas se hayan añadido */}
                {renovaciones.map((ren, idx) => (
                    <div key={ren.id_temp} className="renov-block">
                        <div className="renov-header">
                            <strong>FECHA:</strong> 
                            <input 
                                type="date" 
                                className="mini-input"
                                value={ren.fecha} 
                                onChange={e => {
                                    const n = [...renovaciones]; n[idx].fecha = e.target.value; setRenovaciones(n);
                                }}
                            />
                        </div>
                        <div className="signature-grid">
                            {Object.keys(ren.firmas).map(rol => (
                                <div key={rol} className="sign-box-full">
                                    <p><strong>{rol}</strong></p>
                                    <div className="placeholder-sign">
                                        {ren.firmas[rol].firma_path ? (
                                            <img src={ren.firmas[rol].firma_path} style={{height:'100%'}} alt="sig"/> 
                                        ) : (
                                            <button className="sign-btn-small" onClick={() => handleOpenSignature('renovacion', 'renovacion', idx, rol)}>Firmar</button>
                                        )}
                                    </div>
                                    <input 
                                        placeholder="Nombre" 
                                        className="mini-input-table"
                                        value={ren.firmas[rol].nombre} 
                                        onChange={e => {
                                            const n = [...renovaciones]; n[idx].firmas[rol].nombre = e.target.value; setRenovaciones(n);
                                        }} 
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </ExpandableCard>

            {/* 12. CIERRE FINAL */}
            <ExpandableCard title="Cierre y Entrega de Activo" icon="🏁">
                <div className="signature-grid">
                    {Object.keys(firmasSecciones.cierre).map(rol => (
                        <div key={rol} className="sign-box-full">
                            <p><strong>{rol}</strong></p>
                            <div className="placeholder-sign">
                                {firmasSecciones.cierre[rol]?.firma_path ? (
                                    <img src={firmasSecciones.cierre[rol].firma_path} style={{height:'100%'}} alt="cierre"/> 
                                ) : (
                                    <button className="sign-btn-small" onClick={() => handleOpenSignature('fija', 'cierre', null, rol)}>Firmar</button>
                                )}
                            </div>
                            <input 
                                placeholder="Nombre completo" 
                                className="mini-input-table"
                                value={firmasSecciones.cierre[rol]?.nombre || ''} 
                                onChange={e => updateFirmaData('cierre', rol, 'nombre', e.target.value)} 
                            />
                            {firmasSecciones.cierre[rol]?.fecha_hora && (
                                <div className="timestamp-tag">{firmasSecciones.cierre[rol].fecha_hora}</div>
                            )}
                        </div>
                    ))}
                </div>
            </ExpandableCard>

            {/* FLOTANTE: El Pad para firmar con el dedo o ratón */}
            {showSignature && <SignaturePad onSave={handleSaveSignature} onCancel={() => setShowSignature(false)} />}
        </div>
    );
};

export default DetailsPTWScreen;